<template>
    <v-row
        :density="isDisplayXs ? 'compact' : 'default'"
    >
        <v-col
            cols="12"
        >
            <v-text-field
                v-model="name"
                v-bind="nameProps"
                label="Name on label"
                name="name"
                autocomplete="name"
                hide-details="auto"
            ></v-text-field>
        </v-col>
        <v-col
            cols="6"
        >
            <BodyHeightInput
                v-model="heightIn"
                v-bind="heightInProps"
                unit="imperial"
            ></BodyHeightInput>
        </v-col>
        <v-col
            cols="6"
        >
            <v-autocomplete
                v-model.number="weightLb"
                v-bind="weightLbProps"
                name="weight"
                label="Weight (lb)"
                autocorrect="off"
                type="tel"
                :items="lo_range(35, 500)"
                no-data-text="Weight out of range"
                hide-details="auto"
                auto-select-first
            ></v-autocomplete>
        </v-col>
        <v-col
            cols="6"
        >
            <v-autocomplete
                v-model.number="age"
                v-bind="ageProps"
                name="age"
                label="Age"
                autocorrect="off"
                type="tel"
                :items="lo_range(8, 110)"
                no-data-text="Height out of range"
                hide-details="auto"
                auto-select-first
            ></v-autocomplete>
        </v-col>
        <v-col
            cols="6"
            class="d-flex flex-column align-center justify-start"
        >
            <v-select
                v-if="isDisplayXs"
                v-model="gender"
                v-bind="genderProps"
                class="w-100"
                label="Gender"
                :items="GenderListMinimum"
            ></v-select>
            <GenderSelectorInput
                v-else
                v-model="gender"
                v-bind="genderProps"
                class="gender-selector-input h-100 w-100"
                size="large"
                :density="'default'"
                hide-no-answer
                mandatory
            ></GenderSelectorInput>
        </v-col>
    </v-row>
</template>

<script lang="ts" setup>
    import type {
        CartGuestBodyResponseData
    } from '~/types';
    import * as v from 'valibot';
    import { toTypedSchema } from '@vee-validate/valibot';
    import { range as lo_range } from 'es-toolkit';
    import { useFormValidation } from 'x1-base/composables';
    import { useIsMobile } from '~/composables';
    import BodyHeightInput from '~/components/controls/body/BodyHeightInput/BodyHeightInput.vue';
    // import BodyWeightInput from '~/components/controls/body/BodyWeightInput/BodyWeightInput.vue';
    import GenderSelectorInput from '~/components/controls/GenderSelectorInput/GenderSelectorInput.vue';
    import { GenderListMinimum, garmentGuestBodyModel } from '~/models';
    import { ProductGarmentDefaultNameOnLabel } from '~/constants';


    const props = defineProps<{
        modelValue?: Nilish<CartGuestBodyResponseData> | NullableDeep<CartGuestBodyResponseData>;
    }>();

    const emit = defineEmits<{
        'update:modelValue': [value: Nullable<CartGuestBodyResponseData>];
    }>();

    let isWatcherPaused = false;
    const { isDisplayXs } = useIsMobile();

    const {
        defineField,
        validate: validateForm,
        values: formValues,
        controlledValues: controlledFormValues,
        resetForm
    } = useForm<CartGuestBodyResponseData>({
        validationSchema: toTypedSchema(
            garmentGuestBodyModel
        ),
        initialValues: {
            name: ProductGarmentDefaultNameOnLabel
        }
    });

    const { getDefineFieldOptions } = useFormValidation();
    const vo = () => getDefineFieldOptions('vuetify');

    const [name, nameProps] = defineField('name', vo());
    const [heightIn, heightInProps] = defineField('heightIn', vo());
    const [weightLb, weightLbProps] = defineField('weightLb', vo());
    const [gender, genderProps] = defineField('gender', vo());
    const [age, ageProps] = defineField('age', vo());

    const unWatchModelValue = watch(
        () => props.modelValue,
        (newVal, oldVal) => {
            if (!oldVal && newVal) {
                // @ts-ignore
                resetForm({ values: newVal, touched: false });
            }
        },
        { immediate: true }
    );

    const unWatchFormValues = watch(
        formValues,
        async () => {
            if (!isWatcherPaused) {
                const validation = v.safeParse(garmentGuestBodyModel, controlledFormValues.value);

                if (validation.success) {
                    setCurrent(controlledFormValues.value);
                }
            }
        }
    );

    function setCurrent(item: Nullable<CartGuestBodyResponseData>): void {
        isWatcherPaused = true;
        emit('update:modelValue', item);

        setTimeout(() => {
            isWatcherPaused = false;
        }, 100);
    }

    onBeforeUnmount(() => {
        unWatchFormValues();
        unWatchModelValue();
    });
</script>

<style lang="scss">
.gender-selector-input {
    max-height: 56px;
}
</style>
